import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query'
import { deliveriesApi } from '@/lib/api'
import type { Delivery, DeliveryFormData, DeliveryStockItem } from '@/types'

export const deliveryKeys = {
  all: ['deliveries'] as const,
  lists: () => [...deliveryKeys.all, 'list'] as const,
  list: (params?: { date_from?: string; date_to?: string }) =>
    [...deliveryKeys.lists(), params] as const,
  detail: (id: number) => [...deliveryKeys.all, 'detail', id] as const,
  stock: (params?: { exclude_delivery_id?: number }) =>
    [...deliveryKeys.all, 'stock', params] as const,
}

export function useDeliveries(params?: {
  date_from?: string
  date_to?: string
}): UseQueryResult<Delivery[]> {
  return useQuery({
    queryKey: deliveryKeys.list(params),
    queryFn: async () => {
      const res = await deliveriesApi.list(params)
      return res.data
    },
  })
}

export function useDelivery(id: number): UseQueryResult<Delivery> {
  return useQuery({
    queryKey: deliveryKeys.detail(id),
    queryFn: async () => {
      const res = await deliveriesApi.get(id)
      return res.data
    },
    enabled: id > 0,
  })
}

export function useDeliveryStock(params?: {
  exclude_delivery_id?: number
}): UseQueryResult<DeliveryStockItem[]> {
  return useQuery({
    queryKey: deliveryKeys.stock(params),
    queryFn: async () => {
      const res = await deliveriesApi.getStock(params)
      return res.data
    },
  })
}

export function useCreateDelivery(): UseMutationResult<
  Delivery,
  Error,
  { data: DeliveryFormData; document?: File | null }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ data, document }) => {
      const res = await deliveriesApi.create(data, document)
      return res.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deliveryKeys.lists() })
      queryClient.invalidateQueries({ queryKey: deliveryKeys.stock() })
    },
  })
}

export function useUpdateDelivery(): UseMutationResult<
  Delivery,
  Error,
  { id: number; data: DeliveryFormData; document?: File | null }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data, document }) => {
      const res = await deliveriesApi.update(id, data, document)
      return res.data
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: deliveryKeys.lists() })
      queryClient.invalidateQueries({ queryKey: deliveryKeys.stock() })
      queryClient.invalidateQueries({ queryKey: deliveryKeys.detail(id) })
    },
  })
}
